import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  Chip
} from '@material-ui/core'
import { Add, Delete, Edit, Notifications } from '@material-ui/icons'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'utils/notify'
import organizationSlice, {
  addRepresentative,
  updateRepresentative,
  deleteRepresentative
} from 'store/organization'
import moment from 'moment/moment'
import BasicTable from 'components/BasicTable'
import getTableOptions from 'utils/getTableOptions'
import { renderCol } from 'utils/renderColumns'
import { AttendeeRoles, Collection, orgRepresentativeStatus } from 'constants/index'
import { cloudFunctionsInstance } from 'config/axios'
import RepresentativeForm from './RepresentativeForm'
import ConfirmModal from 'components/ConfirmationModal'
import { chipStyleForOrgRepresentative } from '../utils'
import { FirestoreDatabase } from '../../../../index'
import DataLoading from '../../../../components/DataLoading'

const OrganizationRepresentatives = (props) => {
  const { organizationId, conferenceId, organization } = props
  const dispatch = useDispatch()
  const classes = useStyles()

  const [openRepModal, setOpenRepModal] = useState(false)
  const [modalType, setModalType] = useState('add') // 'add' or 'edit'
  const [editingRep, setEditingRep] = useState(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [isPerformingAction, setIsPerformingAction] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [isNotifying, setIsNotifying] = useState(false)
  const [confirmNotifyModal, setConfirmNotifyModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [loadingReps, setLoadingReps] = useState(false)

  const { representatives } = useSelector((state) => state.organization)

  // Calculate stats for representatives
  const representativeStats = useMemo(() => {
    const total = representatives.length
    const notified = representatives.filter(
      (rep) => rep.status === orgRepresentativeStatus.NOTIFIED
    ).length
    const notNotified = representatives.filter(
      (rep) => rep.status !== orgRepresentativeStatus.NOTIFIED
    ).length

    return {
      total,
      notified,
      notNotified
    }
  }, [representatives])

  useEffect(() => {
    setLoadingReps(true)
    const unsub = FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('organizationId', '==', organizationId)
      .where('role', '==', AttendeeRoles.REPRESENTATIVE)
      .onSnapshot((snapshot) => {
        const representatives = snapshot.docs?.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        dispatch(organizationSlice.actions.setOrganizationRepresentatives(representatives))
        setLoadingReps(false)
      })
    return () => {
      dispatch(organizationSlice.actions.setOrganizationRepresentatives([]))
      unsub()
    }
  }, [organizationId])

  const isPrimaryContactExists = useMemo(() => {
    return representatives.some((rep) => rep.isPrimaryContact)
  }, [representatives, organizationId])

  const initialValues = {
    firstName: editingRep?.firstName || '',
    lastName: editingRep?.lastName || '',
    email: editingRep?.email || '',
    credential: editingRep?.credential || '',
    position: editingRep?.position || '',
    company: organization?.organization || '',
    phone: editingRep?.phone || '',
    city: editingRep?.city || '',
    state: editingRep?.state || '',
    country: editingRep?.country || '',
    isPrimaryContact: editingRep?.isPrimaryContact || false
  }

  // Define table columns
  const tableHead = [
    'First Name',
    'Last Name',
    'Email',
    'Primary Contact',
    {
      name: 'Status',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => (
          <div style={{ textAlign: 'center' }}>{renderCol(meta.label)}</div>
        )
      }
    }
  ]

  const handleRowSelectionChange = (currentRowsSelected, allRowsSelected) => {
    const selected = allRowsSelected.map((row) => row.dataIndex)
    setSelectedRows(selected)
  }

  const handleAddRepresentative = async (values) => {
    try {
      setIsPerformingAction(true)
      if (isPrimaryContactExists && values.isPrimaryContact) {
        toast.warning('A primary contact already exists.')
        return
      }

      // Add new representative as attendee
      const newRep = {
        ...values,
        role: AttendeeRoles.REPRESENTATIVE,
        organizationId: organizationId,
        conferenceId: conferenceId,
        status: orgRepresentativeStatus.NOT_NOTIFIED,
        createdAt: moment().utc().valueOf()
      }
      await addRepresentative(newRep, conferenceId)
    } catch (error) {
      toast.error('Could not add representative. Try again.')
      console.error(error)
    } finally {
      setIsPerformingAction(false)
      setOpenRepModal(false)
    }
  }

  const handleEditRepresentative = async (values) => {
    if (!editingRep || !editingRep.id) return

    try {
      setIsPerformingAction(true)
      await updateRepresentative(editingRep?.id, {
        ...editingRep,
        ...values
      })
      setEditingRep(null)
    } catch (error) {
      toast.error('Could not update representative. Try again.')
    } finally {
      setOpenRepModal(false)
      setIsPerformingAction(false)
    }
  }

  const handleDeleteRepresentative = async () => {
    if (!confirmDeleteId) return

    try {
      setIsPerformingAction(true)
      // Prevent deletion if the representative is a primary contact and organization has submitted data
      if (
        representatives?.find((rep) => rep?.id === confirmDeleteId)?.isPrimaryContact &&
        organization?.submittedAt
      ) {
        toast.warning(
          'Cannot delete the primary contact after organization data has been received.'
        )
        return
      }

      await dispatch(deleteRepresentative(confirmDeleteId))
      toast.success('Representative deleted.')
      setConfirmDeleteId(null)
    } catch (error) {
      toast.error('Could not delete representative. Try again.')
    } finally {
      setIsPerformingAction(false)
    }
  }

  const openEditModal = (rep) => {
    setEditingRep(rep)
    setModalType('edit')
    setOpenRepModal(true)
  }

  const handleNotifySelected = async () => {
    if (!selectedRows.length) {
      toast.warning('No representatives selected.')
      return
    }

    setConfirmNotifyModal(true)
  }

  const tableOptions = {
    ...getTableOptions(),
    selectableRows: 'multiple',
    selectableRowsOnClick: false,
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: false,
    onRowSelectionChange: handleRowSelectionChange,
    rowsSelected: selectedRows.map((dataIndex) =>
      representatives.findIndex((_item, idx) => idx === dataIndex)
    ),
    customToolbarSelect: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '8px 12px' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Notifications />}
          onClick={handleNotifySelected}
          disabled={isNotifying}>
          Notify Representatives
        </Button>
      </Box>
    )
  }

  // Filter representatives based on status
  const filteredRepresentatives = useMemo(() => {
    let filteredReps
    switch (statusFilter) {
      case 'notified':
        filteredReps = representatives.filter(
          (rep) => rep.status === orgRepresentativeStatus.NOTIFIED
        )
        break
      case 'not-notified':
        filteredReps = representatives.filter(
          (rep) => rep.status !== orgRepresentativeStatus.NOTIFIED
        )
        break
      default:
        filteredReps = [...representatives]
        break
    }

    // Sort to ensure primary contact is always at the top
    return filteredReps.sort((a, b) => {
      if (a.isPrimaryContact && !b.isPrimaryContact) return -1
      if (!a.isPrimaryContact && b.isPrimaryContact) return 1
      return 0
    })
  }, [representatives, statusFilter])

  // Format table data
  const formatTableData = useMemo(() => {
    return filteredRepresentatives.map((rep) => [
      rep.firstName,
      rep.lastName,
      rep.email,
      rep.isPrimaryContact ? 'Yes' : 'No',
      <Chip
        size="small"
        label={
          rep?.status === orgRepresentativeStatus.REGISTERED
            ? 'Registered'
            : rep?.status === orgRepresentativeStatus.NOTIFIED
            ? 'Notified'
            : 'Not Notified'
        }
        style={{
          ...chipStyleForOrgRepresentative(rep?.status).style
        }}
      />,
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip title="Edit Representative">
          <IconButton size="small" onClick={() => openEditModal(rep)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Representative">
          <IconButton size="small" onClick={() => setConfirmDeleteId(rep.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    ])
  }, [filteredRepresentatives])

  const sendNotifications = async () => {
    try {
      setIsNotifying(true)
      const selectedRepresentatives = selectedRows.map((index) => filteredRepresentatives[index])
      await cloudFunctionsInstance.post('/inviteOrRemindOrgRepresentatives', {
        representativeIds: selectedRepresentatives.map((rep) => rep.id),
        organizationId: organizationId,
        conferenceId: conferenceId
      })
      toast.success(`${selectedRepresentatives.length} representatives notified.`)
      setConfirmNotifyModal(false)
      setSelectedRows([])
    } catch (error) {
      toast.error('Could not send notifications. Try again.')
      console.error(error)
    } finally {
      setIsNotifying(false)
    }
  }

  if (loadingReps) {
    return <DataLoading text="Loading representatives..." />
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.filterChips}>
          <Chip
            label={`All: ${representativeStats.total}`}
            onClick={() => {
              setSelectedRows([])
              setStatusFilter('all')
            }}
            color={statusFilter === 'all' ? 'primary' : 'default'}
            className={statusFilter === 'all' ? classes.activeChip : classes.chip}
          />
          <Chip
            label={`Not Notified: ${representativeStats.notNotified}`}
            onClick={() => {
              setSelectedRows([])
              setStatusFilter('not-notified')
            }}
            color={statusFilter === 'not-notified' ? 'primary' : 'default'}
            className={statusFilter === 'not-notified' ? classes.activeChip : classes.chip}
          />
          <Chip
            label={`Notified: ${representativeStats.notified}`}
            onClick={() => {
              setSelectedRows([])
              setStatusFilter('notified')
            }}
            color={statusFilter === 'notified' ? 'primary' : 'default'}
            className={statusFilter === 'notified' ? classes.activeChip : classes.chip}
          />
        </Box>
        <div className={classes.actionButtons}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => {
              setModalType('add')
              setOpenRepModal(true)
            }}
            className={classes.actionButton}>
            Add Representative
          </Button>
        </div>
      </Box>

      {filteredRepresentatives.length > 0 ? (
        <Paper variant="outlined">
          <BasicTable data={formatTableData} columns={tableHead} options={tableOptions} />
        </Paper>
      ) : (
        <Box className={classes.emptyState}>
          <Typography variant="body1">
            No representatives{' '}
            {statusFilter !== 'all' ? `with status "${statusFilter}"` : 'added yet'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {statusFilter !== 'all'
              ? 'Try changing the filter'
              : 'Add representatives to manage contacts for this organization'}
          </Typography>
        </Box>
      )}

      {/* Representative Modal - for both Add and Edit */}
      <Dialog
        open={openRepModal}
        onClose={() => {
          if (!isPerformingAction) {
            setOpenRepModal(false)
            setEditingRep(null)
          }
        }}
        maxWidth="xl"
        fullWidth>
        <DialogTitle>
          {modalType === 'add' ? 'Add Representative' : 'Edit Representative'}
        </DialogTitle>
        <DialogContent>
          <RepresentativeForm
            initialValues={initialValues}
            onSubmit={modalType === 'add' ? handleAddRepresentative : handleEditRepresentative}
            onCancel={() => {
              setOpenRepModal(false)
              if (modalType === 'edit') {
                setEditingRep(null)
              }
            }}
            isPerformingAction={isPerformingAction}
            isPrimaryContactExists={
              modalType === 'edit'
                ? representatives.some((rep) => rep.isPrimaryContact && rep.id !== editingRep?.id)
                : isPrimaryContactExists
            }
            representatives={representatives}
            editingRepId={editingRep?.id}
            submitButtonText={modalType === 'add' ? 'Add Representative' : 'Update Representative'}
          />
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        dialogTitle="Confirm Delete"
        dialogText="Are you sure you want to delete this representative? This action cannot be undone."
        open={!!confirmDeleteId}
        closeModal={() => !isPerformingAction && setConfirmDeleteId(null)}
        handleConfirm={handleDeleteRepresentative}
        inProgress={isPerformingAction}
      />

      {/* Confirm Notify Modal */}
      <Dialog
        open={confirmNotifyModal}
        onClose={() => !isNotifying && setConfirmNotifyModal(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Notify Representatives</DialogTitle>
        <DialogContent>
          <Typography>
            {`Are you sure you want to send notifications to ${selectedRows.length} representatives?`}
          </Typography>

          <DialogActions style={{ display: 'flex', gap: '10px', padding: '10px 0px' }}>
            <Button
              onClick={() => !isNotifying && setConfirmNotifyModal(false)}
              disabled={isNotifying}>
              Cancel
            </Button>
            <Button
              onClick={sendNotifications}
              color="primary"
              variant="contained"
              disabled={isNotifying}>
              {isNotifying ? 'Sending...' : 'Notify'}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  actionButton: {
    whiteSpace: 'nowrap'
  },
  filterChips: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  chip: {
    cursor: 'pointer',
    fontWeight: 'normal'
  },
  activeChip: {
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    backgroundColor: '#f5f5f5',
    borderRadius: '4px'
  },
  pdfForm: {
    marginTop: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
    marginBottom: '1rem'
  },
  buttonBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  modalButton: {
    textTransform: 'none',
    letterSpacing: '1px',
    fontSize: '1rem'
  },
  downloadCircle: {
    marginLeft: '0.75rem'
  }
}))

export default React.memo(OrganizationRepresentatives)
